import { config } from '@/config';
import keycloakService, { type User } from '@/services/keycloak.service';

// API response types
export interface SessionResponse {
  user: User;
  expires: string;
}

export interface AuthErrorResponse {
  error?: string;
  message?: string;
}

/**
 * Gets an authentication token if available, initializing and refreshing as needed.
 * Returns undefined if token cannot be obtained (proceeds without auth).
 */
export async function getAuthToken(): Promise<string | undefined> {
  try {
    if (!keycloakService.isInitialized()) {
      await keycloakService.init();
    }
    if (keycloakService.isAuthenticated()) {
      // Try to refresh token if it expires within 30 seconds (fails silently if refresh fails)
      try {
        await keycloakService.updateToken(30);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: using in web-app
        console.warn('[API Call] Token refresh failed, using existing token:', error);
      }
      return keycloakService.getToken();
    }
  } catch (e) {
    // biome-ignore lint/suspicious/noConsole: using in web-app
    console.warn('[API Call] Keycloak initialization failed:', e);
    // If init fails, proceed without token; caller may trigger login
  }
  return undefined;
}

// API helper function with Keycloak token support
const apiCall = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const url = `${config.api.baseURL}${endpoint}`;
  const isFormData = options.body instanceof FormData;

  // Get auth token
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };

  // Only set Content-Type if not FormData (browser will set multipart/form-data boundary)
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  // Add existing headers
  if (options.headers) {
    if (Array.isArray(options.headers)) {
      options.headers.forEach(([key, value]) => {
        headers[key] = value;
      });
    } else if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else {
      Object.assign(headers, options.headers);
    }
  }

  // Add Authorization header if token is available
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // For FormData, we need to let the browser set Content-Type with boundary
  // So we create a Headers object and only add non-Content-Type headers
  let fetchHeaders: HeadersInit;
  if (isFormData) {
    // Only include Authorization and other headers, NOT Content-Type
    fetchHeaders = new Headers();
    Object.keys(headers).forEach((key) => {
      if (key !== 'Content-Type') {
        (fetchHeaders as Headers).set(key, headers[key]);
      }
    });
  } else {
    fetchHeaders = headers;
  }

  const baseOptions: RequestInit = {
    headers: fetchHeaders,
    credentials: 'include', // Still include cookies for backward compatibility
    cache: 'no-store', // avoid 304 responses without bodies
  };

  // Merge headers properly to avoid overwrites
  const mergedOptions: RequestInit = {
    ...baseOptions,
    ...options,
    headers: fetchHeaders, // Always use our auth-enriched headers
  };

  let response = await fetch(url, mergedOptions);

  // If we still get a 304 (no body), retry once forcing reload
  if (response.status === 304) {
    response = await fetch(url, { ...mergedOptions, cache: 'reload' });
  }

  if (!response.ok) {
    // On 401, attempt a single silent refresh + retry when authenticated
    if (response.status === 401) {
      try {
        if (!keycloakService.isInitialized()) {
          await keycloakService.init();
        }
        if (keycloakService.isAuthenticated()) {
          await keycloakService.updateToken(0); // force refresh
          const refreshedToken = keycloakService.getToken();
          if (refreshedToken) {
            const retryHeaders = { ...headers, Authorization: `Bearer ${refreshedToken}` };
            const retryOptions: RequestInit = {
              ...mergedOptions,
              headers: retryHeaders,
            };
            response = await fetch(url, retryOptions);
          }
        }
      } catch (_e) {
        // fall through to error handling
      }
    }

    if (!response.ok) {
      const errorData: AuthErrorResponse = await response
        .json()
        .catch(() => ({ error: 'Network error occurred' }));
      const message = errorData.error || errorData.message || `HTTP ${response.status}`;
      throw new Error(message);
    }
  }

  const body = await response.json();

  // Normalize common API shape: { success: boolean, data: T }
  // BUT: If the response has pagination at top level alongside data, don't unwrap
  // This preserves both data and pagination for paginated endpoints
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    // Check if pagination exists at the same level as data
    if ('pagination' in body) {
      // Return the full response (keeps both data and pagination)
      return body as T;
    }
    // No pagination - unwrap to just the data
    return (body as { data: T }).data;
  }

  return body as T;
};

// Session management for Keycloak
export const getSession = async (): Promise<SessionResponse | null> => {
  try {
    if (!keycloakService.isInitialized() || !keycloakService.isAuthenticated()) {
      return null;
    }

    const user = keycloakService.getCurrentUser();
    if (!user) {
      return null;
    }

    // Create a mock session response compatible with your existing system
    return {
      user,
      expires: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
    };
  } catch (_error) {
    return null;
  }
};

// Login redirect (handled by Keycloak)
export const login = async (): Promise<void> => {
  return keycloakService.login();
};

// Logout (handled by Keycloak)
export const logout = async (): Promise<{ message: string }> => {
  await keycloakService.logout();
  return { message: 'Logged out successfully' };
};

// Dashboard metrics with Keycloak auth
export const getDashboardMetrics = async () => {
  return apiCall<{
    totalRevenue: number;
    totalPayments: number;
    pendingPayments: number;
    completedPayments: number;
  }>('/metrics/dashboard');
};

// Payment activity with Keycloak auth
export const getPaymentActivity = async () => {
  return apiCall<Array<{ date: string; amount: number }>>('/metrics/payment-activity');
};

// Generic authenticated API call helper
export const authenticatedApiCall = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  return apiCall<T>(endpoint, options);
};

// Helper to check if user has required role for API calls
export const checkUserRole = (requiredRole: string): boolean => {
  return keycloakService.hasRole(requiredRole);
};

// Get current user roles
export const getCurrentUserRoles = (): string[] => {
  return keycloakService.getUserRoles();
};

/** Current user as returned by GET /api/users/current-user (includes partyId from DB) */
export interface CurrentUserApiResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  partyId: string;
  type: 'basic' | 'internal' | 'admin';
  phoneNumber?: string;
}

/** Fetch current user from API to get partyId (and other app data). Use to populate user.partyId in auth context. */
export const getCurrentUserFromApi = async (): Promise<CurrentUserApiResponse | null> => {
  try {
    if (!keycloakService.isInitialized() || !keycloakService.isAuthenticated()) {
      return null;
    }
    return await apiCall<CurrentUserApiResponse>('/users/current-user');
  } catch {
    return null;
  }
};

export default {
  getSession,
  login,
  logout,
  getDashboardMetrics,
  getPaymentActivity,
  authenticatedApiCall,
  checkUserRole,
  getCurrentUserRoles,
  getCurrentUserFromApi,
};
